import React, { useState, useEffect } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useAlert } from "../../contexts/AlertContext";
import "./Signup.css";
import { CanvasContext } from "../../contexts/CanvasContext";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaExclamationCircle,
} from "react-icons/fa";

const initialValues = {
  name: "",
  email: "",
  mobile: "",
  password: "",
  cpassword: "",
  otp: "",
};

const baseValidationSchema = Yup.object({
  name: Yup.string()
    .required("Name is required")
    .matches(/^[^0-9]*$/, "Name must not contain numeric characters")
    .min(3, "Name should be minimum of 3 characters"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  mobile: Yup.string()
    .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits")
    .required("Mobile number is required"),
  password: Yup.string()
    .required("Password is required")
    .matches(
      /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,}$/,
      "Password must contain at least one letter, one number, and one special character, and be at least 6 characters long"
    ),
  cpassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Confirm Password is required"),
});

const otpValidationSchema = baseValidationSchema.shape({
  otp: Yup.string()
    .matches(/^[0-9]{6}$/, "OTP must be exactly 6 digits")
    .required("OTP is required"),
});

const SignUp = () => {
  const { isLogin } = useAuth();
  const { setAlert } = useAlert();
  const { isDarkTheme } = React.useContext(CanvasContext);
  const navigate = useNavigate();
  const [otpSent, setOtpSent] = useState(false);
  const [otpExpiration, setOtpExpiration] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const sendOTP = async (values) => {
    try {
      const response = await fetch(
        // "http://localhost:5000/api/auth/send-otp",
        "https://dnd-back.vercel.app/api/auth/send-otp",
        // "https://nexgensitesserver-git-main-princepatel20s-projects.vercel.app/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: values.name,
            mobile: values.mobile,
            email: values.email,
            password: values.password,
          }),
        }
      );
      const json = await response.json();
      if (json.success) {
        setOtpSent(true);
        setOtpExpiration(json.expirationTime);
        setAlert({
          title: "OTP Sent",
          message: "Please check your email for the OTP. Valid for 1 minute.",
          variant: "success",
          duration: 1000,
        });
      } else {
        setAlert({
          title: "OTP Sending Failed",
          message: json.error,
          variant: "error",
          duration: 1000,
        });
      }
    } catch (error) {
      setAlert({
        title: "An error occurred",
        message: "Please try again later.",
        variant: "error",
        duration: 1000,
      });
      console.error("An error occurred:", error);
    }
  };

  const verifyOTP = async (values) => {
    try {
      const response = await fetch(
        // "http://localhost:5000/api/auth/verify-otp",
        "https://dnd-back.vercel.app/api/auth/verify-otp",
        // "https://nexgensitesserver-git-main-princepatel20s-projects.vercel.app/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(values),
        }
      );
      const json = await response.json();
      if (json.success) {
        navigate("/signin");
        setAlert({
          title: "You have successfully Signed up!",
          message: "Please login to continue",
          variant: "success",
          duration: 1000,
        });
      } else {
        setAlert({
          title: "Sign up Failed",
          message: json.error,
          variant: "error",
          duration: 1000,
        });
      }
    } catch (error) {
      setAlert({
        title: "An error occurred",
        message: "Please try again later.",
        variant: "error",
        duration: 1000,
      });
      console.error("An error occurred:", error);
    }
  };

  useEffect(() => {
    if (isLogin) {
      navigate("/");
    }
  }, [isLogin, navigate]);

  useEffect(() => {
    let timer;
    if (otpExpiration) {
      timer = setInterval(() => {
        const now = Date.now();
        const remaining = Math.max(0, otpExpiration - now);
        setTimeLeft(Math.ceil(remaining / 1000));

        if (remaining <= 0) {
          clearInterval(timer);
          setOtpSent(false);
          setAlert({
            title: "OTP Expired",
            message: "Please request a new OTP",
            variant: "warning",
            duration: 1000,
          });
          navigate("/signup");
        }
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [otpExpiration, setAlert, navigate]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <React.Fragment>
      {!isLogin && (
        <section
          className={`signup-page ${isDarkTheme ? "dark-theme" : ""}`}
          id="signup"
        >
          <div className="signup-container">
            <header className="signup-header">
              <h1>Let's start the journey</h1>
              <p>Create your account and begin building amazing websites</p>
            </header>
            <Formik
              initialValues={initialValues}
              validationSchema={
                otpSent ? otpValidationSchema : baseValidationSchema
              }
              onSubmit={otpSent ? verifyOTP : sendOTP}
              enableReinitialize
            >
              {(formik) => (
                <Form className="signup-form">
                  {!otpSent && (
                    <>
                      <div className="form-group">
                        <label htmlFor="name">
                          <FaUser size={20} />
                          Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          autoComplete="name"
                          {...formik.getFieldProps("name")}
                          className={
                            formik.touched.name && formik.errors.name
                              ? "input-error"
                              : ""
                          }
                        />
                        {formik.touched.name && formik.errors.name && (
                          <div className="error">
                            <FaExclamationCircle />
                            {formik.errors.name}
                          </div>
                        )}
                      </div>
                      <div className="form-group">
                        <label htmlFor="email">
                          <FaEnvelope size={20} />
                          Email
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          autoComplete="email"
                          {...formik.getFieldProps("email")}
                          className={
                            formik.touched.email && formik.errors.email
                              ? "input-error"
                              : ""
                          }
                        />
                        {formik.touched.email && formik.errors.email && (
                          <div className="error">{formik.errors.email}</div>
                        )}
                      </div>
                      <div className="form-group">
                        <label htmlFor="mobile">
                          <FaPhone size={20} />
                          Mobile
                        </label>
                        <input
                          type="tel"
                          id="mobile"
                          name="mobile"
                          autoComplete="tel"
                          {...formik.getFieldProps("mobile")}
                          className={
                            formik.touched.mobile && formik.errors.mobile
                              ? "input-error"
                              : ""
                          }
                        />
                        {formik.touched.mobile && formik.errors.mobile && (
                          <div className="error">{formik.errors.mobile}</div>
                        )}
                      </div>
                      <div className="form-group">
                        <label htmlFor="password">
                          <FaLock size={20} />
                          Password
                        </label>
                        <div className="password-input">
                          <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            name="password"
                            autoComplete="new-password"
                            {...formik.getFieldProps("password")}
                            className={
                              formik.touched.password && formik.errors.password
                                ? "input-error"
                                : ""
                            }
                          />
                          <button
                            type="button"
                            onClick={togglePasswordVisibility}
                            className="password-toggle"
                          >
                            {showPassword ? (
                              <FaEyeSlash size={20} />
                            ) : (
                              <FaEye size={20} />
                            )}
                          </button>
                        </div>
                        {formik.touched.password && formik.errors.password && (
                          <div className="error">{formik.errors.password}</div>
                        )}
                      </div>
                      <div className="form-group">
                        <label htmlFor="cpassword">
                          <FaLock size={20} />
                          Confirm Password
                        </label>
                        <div className="password-input">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            id="cpassword"
                            name="cpassword"
                            autoComplete="new-password"
                            {...formik.getFieldProps("cpassword")}
                            className={
                              formik.touched.cpassword &&
                              formik.errors.cpassword
                                ? "input-error"
                                : ""
                            }
                          />
                          <button
                            type="button"
                            onClick={toggleConfirmPasswordVisibility}
                            className="password-toggle"
                          >
                            {showConfirmPassword ? (
                              <FaEye size={20} />
                            ) : (
                              <FaEyeSlash size={20} />
                            )}
                          </button>
                        </div>
                        {formik.touched.cpassword &&
                          formik.errors.cpassword && (
                            <div className="error">
                              {formik.errors.cpassword}
                            </div>
                          )}
                      </div>
                    </>
                  )}
                  {otpSent && (
                    <>
                      <div className="form-group">
                        <label htmlFor="otp">Enter OTP</label>
                        <input
                          type="text"
                          id="otp"
                          name="otp"
                          autoComplete="one-time-code"
                          {...formik.getFieldProps("otp")}
                          className={
                            formik.touched.otp && formik.errors.otp
                              ? "input-error"
                              : ""
                          }
                        />
                        {formik.touched.otp && formik.errors.otp && (
                          <div className="error">{formik.errors.otp}</div>
                        )}
                      </div>
                      <div className="otp-timer">
                        Time remaining: {timeLeft} seconds
                      </div>
                    </>
                  )}
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={!formik.isValid || formik.isSubmitting}
                  >
                    {formik.isSubmitting ? (
                      <div className="loading-spinner">
                        Loading...
                      </div>
                    ) : otpSent ? (
                      "Verify OTP"
                    ) : (
                      "Create Account"
                    )}
                  </button>
                  <div className="signup-link">
                    <Link to="/signin">Already have an account? Login</Link>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </section>
      )}
    </React.Fragment>
  );
};

export default SignUp;
